// ** React Imports
import { useContext, useMemo } from "react";

// ** Redux & Store & Actions
import { useDispatch, useSelector } from "react-redux";
import { setLanguageAsync } from "store/modules/global-common.slice";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// ** Custom Components
import Text from "components/typography/Text";
import Dropdown from "components/layout/Dropdown";
import MenuList from "components/layout/Menu/List";
import MenuItem from "components/layout/Menu/Item";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";
import Block from "components/layout/Block";
import { Icon } from "components/icons";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";

// ** Utils & Helpers
import { LANG } from "helpers/constants";
import { overflowChanger } from "helpers/overflowChanger";
import removeCookie from "helpers/removeCookie";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import { borderRadiuses } from "styles/declares";

// ** Config
import configGlobal from "configuration";

// #####################################################

const StyledButton = styled.button`
	padding: 0.25rem 0.5rem;
	border-radius: ${({ theme: { borderRadius } }) => borderRadius.element};
	user-select: none;
	margin-left: -0.5rem;
	font-family: Arial, Helvetica, sans-serif;

	&:hover {
		background-color: ${({ theme: { colors } }) => colors.itemsHover};
	}
`;

// #####################################################

const LangSelector = ({ parentIsFooter = false }) => {
	const { t } = useTranslation(["common"]);

	const dispatch = useDispatch();

	const { isPhone } = useContext(deviceTypeContext);
	const { isOpen, close, toggle } = useOpenClose();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const language = useSelector((state) => state.globalCommon.language);

	const extendedClose = ({ insideUseEffect = false } = {}) => {
		if (insideUseEffect) {
			close();
		} else {
			close(() => {
				if (parentIsFooter) {
					overflowChanger({ conditionName: "ifIsHidden" });
				}
			});
		}
	};

	const extendedToggle = () => {
		toggle(() => {
			if (!isOpen && isPhone && parentIsFooter) {
				overflowChanger({ conditionName: "ifIsNotHidden" });
			} else if (isOpen && parentIsFooter) {
				overflowChanger({ conditionName: "ifIsHidden" });
			}
		});
	};

	const useLayerOptions = {
		overflowContainer: true,
		auto: true,
		possiblePlacements: ["bottom-start", "top-start"],
		triggerOffset: 5,
	};

	const customLayerStyle = {
		zIndex: 1000,
		borderRadius: `${borderRadiuses.card}rem`,
		borderTopRightRadius: `${borderRadiuses.card}rem`,
		borderTopLeftRadius: `${borderRadiuses.card}rem`,
	};

	const displayText = useMemo(() => {
		const languageIndex = LANG.findIndex(
			(languageItem) => languageItem.value === language,
		);

		return languageIndex >= 0
			? LANG[languageIndex].content
			: LANG[1].content;
	}, [language]);

	// #####################################################

	return (
		<Dropdown
			isOpen={isOpen}
			close={(insideUseEffect) => {
				extendedClose(insideUseEffect);
			}}
			useLayerOptions={useLayerOptions}
			customLayerStyle={customLayerStyle}
			withMobileVersion={true}
			mobileTitle={t("common:language")}
			customMobileLayerStyle={{
				left: parentIsFooter ? "1rem" : "2rem",
				right: parentIsFooter ? "1rem" : "2rem",
			}}
			isSingleDropdown={parentIsFooter}
			minWidth
			menuContent={() => (
				<MenuList isSelector>
					{LANG.map((item) => {
						return (
							<MenuItem
								tabIndex={0}
								rel="nofollow"
								onClick={() => {
									// TODO: Do usunięcia za jakiś czas jak użytkownicy wyczyszczą ciasteczka z www.datezone.com
									removeCookie(
										configGlobal.language.cookieName,
										"www.datezone.com",
									);
									removeCookie(
										configGlobal.languagePref.cookieName,
										"www.datezone.com",
									);
									dispatch(
										setLanguageAsync({
											language: item.value,
											auth: isAuth,
											setCookie: true,
										}),
									);
									extendedClose();
								}}
								cliclable
								key={item.value || item.key}
								noMarginTop
							>
								<MenuItem.Content $customPadding="0.5rem 1rem">
									<Text
										size={"0.9rem"}
										bold={item.value === language}
									>
										{typeof item.content === "function"
											? item.content()
											: item.content}
									</Text>
								</MenuItem.Content>
							</MenuItem>
						);
					})}
				</MenuList>
			)}
		>
			<DropdownTriggerContext.Consumer>
				{({ triggerProps }) => (
					<StyledButton
						onClick={extendedToggle}
						aria-haspopup="true"
						aria-expanded={isOpen.toString()}
						{...triggerProps}
					>
						<Block>
							<Text color="grey" mr={2} size="0.9rem">
								{t("common:language")}
							</Text>
							<Text
								color="primary"
								size="0.9rem"
								style={{ textWrap: "nowrap" }}
							>
								<Text ml={1} mr={2}>
									{displayText}
								</Text>
								<Icon
									name={isOpen ? "caret-up" : "caret-down"}
								/>
							</Text>
						</Block>
					</StyledButton>
				)}
			</DropdownTriggerContext.Consumer>
		</Dropdown>
	);
};

// #####################################################

export default LangSelector;

// #####################################################
